import React, { useState, useRef, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  FlatList,
  ListRenderItemInfo,
  ActivityIndicator,
} from 'react-native';
import Modal from '../components/Modal';
import { SafeAreaView } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { getProfiles, getProfileByCode, getGotras, getStates, getDistricts, getEducations, getCommunities, getProfessions } from '../services/api';
import { ProfileCard, THEME } from '../components';
import { RELIGIONS, JOB_TYPES, MARITAL_STATUSES, DIET_OPTIONS, SMOKING_OPTIONS, DRINKING_OPTIONS, INCOME_FILTER_OPTIONS } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import type {
  Profile, SearchFilters, GotraMasterResponse,
  StateResponse, DistrictResponse, EducationMasterResponse,
  CommunitySurnameResponse, ProfessionMasterResponse,
} from '../types';

const PAGE_SIZE = 5;
const MANGLIK_OPTIONS = ['Any', 'Yes', 'No'];
const RELIGION_OPTIONS = ['Any', ...RELIGIONS];
const JOB_TYPE_FILTERS = ['Any', ...JOB_TYPES];
const MARITAL_STATUS_FILTERS = ['Any', ...MARITAL_STATUSES];
const DIET_FILTERS = ['Any', ...DIET_OPTIONS];
const SMOKING_FILTERS = ['Any', ...SMOKING_OPTIONS];
const DRINKING_FILTERS = ['Any', ...DRINKING_OPTIONS];

const DEFAULT_FILTERS: SearchFilters = {
  gender: '',
  religion: 'Any',
  caste: '',
  casteSubType: '',
  stateName: '',
  district: '',
  gotra: '',
  highestEducation: '',
  eduSubCategory: '',
  jobType: 'Any',
  jobSector: 'Any',
  job: 'Any',
  manglik: 'Any',
  diet: 'Any',
  smoking: 'Any',
  drinking: 'Any',
  profileCode: '',
  maritalStatus: 'Any',
  minAge: '',
  maxAge: '',
  minAnnualIncome: 0,
};

// ── DropdownPicker ────────────────────────────────────────────────────────────
interface DropdownPickerProps {
  label: string;
  value: string;
  options: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  labelMap?: Record<string, string>;
  onSelect: (val: string) => void;
}

const DropdownPicker: React.FC<DropdownPickerProps> = ({
  label, value, options, placeholder, disabled, loading, labelMap, onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const hasValue = !!value;
  const getLabel = (v: string) => labelMap?.[v] ?? v;
  const filtered = options.filter(o => getLabel(o).toLowerCase().includes(search.toLowerCase()));

  const handleSelect = (val: string) => {
    onSelect(val === 'Any' ? '' : val);
    setOpen(false);
    setSearch('');
  };

  return (
    <View style={styles.chipSection}>
      <Text style={styles.filterLabel}>{label}</Text>
      <TouchableOpacity
        style={[styles.dropdownBtn, hasValue && styles.dropdownBtnActive, disabled && styles.dropdownBtnDisabled]}
        onPress={() => { if (!disabled && !loading) { setSearch(''); setOpen(true); } }}
        activeOpacity={0.8}>
        {loading ? (
          <ActivityIndicator size="small" color={THEME.primary} style={{ marginRight: 8 }} />
        ) : null}
        <Text style={[styles.dropdownBtnText, hasValue && styles.dropdownBtnTextActive]} numberOfLines={1}>
          {hasValue ? getLabel(value) : (placeholder ?? `Any ${label}`)}
        </Text>
        <Icon name="chevron-down" size={16} color={hasValue ? THEME.primary : THEME.textLight} />
      </TouchableOpacity>

      <Modal visible={open} animationType="slide" transparent onRequestClose={() => setOpen(false)}>
        <View style={styles.modalOverlay}>
          <SafeAreaView style={styles.modalSheet}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{label}</Text>
              <TouchableOpacity onPress={() => { setOpen(false); setSearch(''); }} style={{ padding: 10 }}>
                <Icon name="close" size={22} color={THEME.text} />
              </TouchableOpacity>
            </View>
            <View style={styles.modalSearch}>
              <Icon name="search-outline" size={18} color={THEME.textLight} />
              <TextInput
                style={styles.modalSearchInput}
                value={search}
                onChangeText={setSearch}
                placeholder={`Search ${label}...`}
                placeholderTextColor={THEME.textLight}
                autoFocus
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')}>
                  <Icon name="close-circle" size={18} color={THEME.textLight} />
                </TouchableOpacity>
              )}
            </View>
            <FlatList
              data={filtered}
              keyExtractor={item => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[styles.modalOption, value === item && item !== 'Any' && styles.modalOptionActive]}
                  onPress={() => handleSelect(item)}>
                  <Text style={[styles.modalOptionText, value === item && item !== 'Any' && styles.modalOptionTextActive]}>
                    {getLabel(item)}
                  </Text>
                  {value === item && item !== 'Any' && (
                    <Icon name="checkmark" size={18} color={THEME.primary} />
                  )}
                </TouchableOpacity>
              )}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ paddingBottom: 40 }}
              ListEmptyComponent={
                <View style={styles.modalEmpty}>
                  <Icon name="search-outline" size={32} color={THEME.border} />
                  <Text style={styles.modalEmptyText}>No results for "{search}"</Text>
                </View>
              }
            />
          </SafeAreaView>
        </View>
      </Modal>
    </View>
  );
};

// ── ChipGroup ─────────────────────────────────────────────────────────────────
interface ChipGroupProps {
  label: string;
  options: readonly string[];
  value: string;
  onChange: (val: string) => void;
}

const ChipGroup: React.FC<ChipGroupProps> = ({ label, options, value, onChange }) => (
  <View style={styles.chipSection}>
    <Text style={styles.filterLabel}>{label}</Text>
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View style={styles.chipRow}>
        {options.map(opt => (
          <TouchableOpacity
            key={opt}
            style={[styles.chip, value === opt && styles.chipActive]}
            onPress={() => onChange(opt)}>
            <Text style={[styles.chipText, value === opt && styles.chipTextActive]}>{opt}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  </View>
);

// ── AgeRange ──────────────────────────────────────────────────────────────────
interface AgeRangeProps {
  label: string;
  minAge: string;
  maxAge: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
}

const MIN_VALID_AGE = 18;
const MAX_VALID_AGE = 80;

const AgeRange: React.FC<AgeRangeProps> = ({ label, minAge, maxAge, onMinChange, onMaxChange }) => {
  const minVal = minAge ? parseInt(minAge, 10) : null;
  const maxVal = maxAge ? parseInt(maxAge, 10) : null;
  const isInvalid = minVal !== null && maxVal !== null && minVal > maxVal;

  const handleMinBlur = () => {
    if (!minAge) return;
    let v = parseInt(minAge, 10);
    if (isNaN(v)) { onMinChange(''); return; }
    v = Math.max(MIN_VALID_AGE, Math.min(MAX_VALID_AGE, v));
    onMinChange(String(v));
    if (maxAge && parseInt(maxAge, 10) < v) onMaxChange(String(v));
  };

  const handleMaxBlur = () => {
    if (!maxAge) return;
    let v = parseInt(maxAge, 10);
    if (isNaN(v)) { onMaxChange(''); return; }
    v = Math.max(MIN_VALID_AGE, Math.min(MAX_VALID_AGE, v));
    onMaxChange(String(v));
    if (minAge && parseInt(minAge, 10) > v) onMinChange(String(v));
  };

  return (
    <View style={styles.chipSection}>
      <Text style={styles.filterLabel}>{label}</Text>
      <View style={styles.ageRow}>
        <TextInput
          style={[styles.ageInput, !!minAge && !isInvalid && styles.ageInputActive, isInvalid && styles.ageInputError]}
          keyboardType="numeric"
          value={minAge}
          onChangeText={v => onMinChange(v.replace(/\D/g, ''))}
          onBlur={handleMinBlur}
          placeholder="Min"
          placeholderTextColor={THEME.textLight}
          maxLength={2}
        />
        <View style={styles.ageDivider} />
        <TextInput
          style={[styles.ageInput, !!maxAge && !isInvalid && styles.ageInputActive, isInvalid && styles.ageInputError]}
          keyboardType="numeric"
          value={maxAge}
          onChangeText={v => onMaxChange(v.replace(/\D/g, ''))}
          onBlur={handleMaxBlur}
          placeholder="Max"
          placeholderTextColor={THEME.textLight}
          maxLength={2}
        />
        <Text style={styles.ageUnit}>yrs</Text>
      </View>
      {isInvalid && <Text style={styles.ageErrorText}>Min age cannot exceed max age</Text>}
    </View>
  );
};

// ── SearchScreen ──────────────────────────────────────────────────────────────
const SearchScreen: React.FC<{ navigation: any }> = ({ navigation }) => {
  const { t } = useLanguage();
  const [filters, setFilters] = useState<SearchFilters>(DEFAULT_FILTERS);
  const [results, setResults] = useState<Profile[]>([]);
  const [searched, setSearched] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false);
  const [codeMode, setCodeMode] = useState<boolean>(false);
  const [profileCode, setProfileCode] = useState<string>('');

  // Master data
  const [states, setStates] = useState<StateResponse[]>([]);
  const [stateOptions, setStateOptions] = useState<string[]>(['Any']);
  const [districts, setDistricts] = useState<DistrictResponse[]>([]);
  const [districtOptions, setDistrictOptions] = useState<string[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [educations, setEducations] = useState<EducationMasterResponse[]>([]);
  const [gotras, setGotras] = useState<GotraMasterResponse[]>([]);
  const [gotraOptions, setGotraOptions] = useState<string[]>(['Any']);
  const [communities, setCommunities] = useState<CommunitySurnameResponse[]>([]);
  const [professionSectors, setProfessionSectors] = useState<ProfessionMasterResponse[]>([]);

  const activeFiltersRef = useRef<SearchFilters>(DEFAULT_FILTERS);

  useEffect(() => {
    // Load gender + caste defaults from AsyncStorage (set by AuthContext after login)
    AsyncStorage.multiGet(['pref_looking_for', 'pref_search_religion', 'pref_search_caste']).then(pairs => {
      const lookingFor = pairs[0][1];
      const religion = pairs[1][1];
      const caste = pairs[2][1];
      setFilters(f => ({
        ...f,
        ...(lookingFor === 'MALE' || lookingFor === 'FEMALE' ? { gender: lookingFor } : {}),
        ...(religion ? { religion } : {}),
        ...(caste ? { caste } : {}),
      }));
    });
    getStates()
      .then(res => {
        setStates(res.data ?? []);
        setStateOptions(['Any', ...(res.data ?? []).map((s: StateResponse) => s.en)]);
      })
      .catch(() => {});

    getEducations()
      .then(res => setEducations((res.data ?? []).filter((e: EducationMasterResponse) => e.active)))
      .catch(() => {});

    getGotras()
      .then(res => {
        const data: GotraMasterResponse[] = res.data ?? [];
        setGotras(data);
        setGotraOptions(['Any', ...data.filter(g => g.active).map(g => g.gotra.en).filter(Boolean)]);
      })
      .catch(() => {});

    getCommunities()
      .then(res => setCommunities(res.data ?? []))
      .catch(() => {});

    getProfessions()
      .then(res => setProfessionSectors((res.data ?? []).filter((p: ProfessionMasterResponse) => p.active)))
      .catch(() => {});
  }, []);

  const setFilter = <K extends keyof SearchFilters>(key: K, val: SearchFilters[K]): void => {
    setFilters(f => ({ ...f, [key]: val }));
  };

  const onStateSelected = (stateName: string) => {
    setFilter('stateName', stateName);
    setFilter('district', '');
    setDistrictOptions([]);
    if (!stateName) return;
    const state = states.find(s => s.en === stateName);
    if (!state) return;
    setLoadingDistricts(true);
    getDistricts(state.stateCode)
      .then(res => {
        const data: DistrictResponse[] = res.data ?? [];
        setDistricts(data);
        setDistrictOptions(data.map(d => d.en));
      })
      .catch(() => {})
      .finally(() => setLoadingDistricts(false));
  };

  const onReligionChange = (val: string) => {
    setFilters(f => ({ ...f, religion: val, caste: '', casteSubType: '' }));
  };

  const onCasteChange = (val: string) => {
    setFilters(f => ({ ...f, caste: val, casteSubType: '' }));
  };

  const selectedReligion = filters.religion && filters.religion !== 'Any' ? filters.religion : '';
  const casteOptions = [
    'Any',
    ...communities
      .filter(c => c.active && c.religion === selectedReligion)
      .map(c => c.community.en),
  ];

  const selectedCommunity = communities.find(c => c.active && c.community.en === filters.caste);
  const subgroupOptions = selectedCommunity
    ? ['Any', ...selectedCommunity.subgroups.map(s => s.name.en)]
    : [];

  const buildParams = (f: SearchFilters, page: number): Record<string, unknown> => {
    const params: Record<string, unknown> = { page };
    if (f.gender) params.gender = f.gender;
    if (f.religion && f.religion !== 'Any') params.religion = f.religion;
    if (f.caste) params.caste = f.caste;
    if (f.casteSubType) params.casteSubType = f.casteSubType;
    if (f.stateName) params.stateName = f.stateName;
    if (f.district) params.district = f.district;
    if (f.gotra) params.gotra = f.gotra;
    if (f.highestEducation) params.highestEducation = f.highestEducation;
    if (f.eduSubCategory) params.eduSubCategory = f.eduSubCategory;
    if (f.jobType !== 'Any') params.jobType = f.jobType;
    if (f.jobSector && f.jobSector !== 'Any') params.jobSector = f.jobSector;
    if (f.job && f.job !== 'Any') params.job = f.job;
    if (f.manglik !== 'Any') params.manglik = f.manglik === 'Yes';
    if (f.maritalStatus && f.maritalStatus !== 'Any') params.maritalStatus = f.maritalStatus;
    if (f.diet && f.diet !== 'Any') params.diet = f.diet;
    if (f.smoking && f.smoking !== 'Any') params.smoking = f.smoking;
    if (f.drinking && f.drinking !== 'Any') params.drinking = f.drinking;
    if (f.minAge) params.minAge = parseInt(f.minAge, 10);
    if (f.maxAge) params.maxAge = parseInt(f.maxAge, 10);
    if (f.minAnnualIncome > 0) params.minAnnualIncome = f.minAnnualIncome;
    return params;
  };

  const activeFilterCount = [
    filters.religion !== 'Any' ? filters.religion : '',
    filters.caste,
    filters.casteSubType,
    filters.stateName,
    filters.district,
    filters.gotra,
    filters.highestEducation,
    filters.eduSubCategory,
    filters.jobType !== 'Any' ? filters.jobType : '',
    filters.jobSector !== 'Any' ? filters.jobSector : '',
    filters.job !== 'Any' ? filters.job : '',
    filters.manglik !== 'Any' ? filters.manglik : '',
    filters.maritalStatus !== 'Any' ? filters.maritalStatus : '',
    filters.diet !== 'Any' ? filters.diet : '',
    filters.smoking !== 'Any' ? filters.smoking : '',
    filters.drinking !== 'Any' ? filters.drinking : '',
    filters.minAge,
    filters.maxAge,
    filters.minAnnualIncome > 0 ? String(filters.minAnnualIncome) : '',
  ].filter(Boolean).length;

  const handleCodeSearch = async (): Promise<void> => {
    const code = profileCode.trim();
    if (!code) return;
    setLoading(true);
    try {
      const res = await getProfileByCode(code);
      if (res.data) {
        navigation.navigate('ProfileDetail', { profileId: res.data.id });
      }
    } catch (e: any) {
      if (e?.response?.status === 404) {
        Toast.show({ type: 'error', text1: 'Profile not found', text2: `No profile with ID "${code}"` });
      } else {
        Toast.show({ type: 'error', text1: 'Search failed', text2: (e as Error).message });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (): Promise<void> => {
    setLoading(true);
    activeFiltersRef.current = filters;
    try {
      const res = await getProfiles(buildParams(filters, 0) as any);
      const profiles: Profile[] = Array.isArray(res.data) ? res.data : [];
      setResults(profiles);
      setCurrentPage(0);
      setHasReachedEnd(profiles.length < PAGE_SIZE);
      setSearched(true);
      setShowFilters(false);
    } catch (e: any) {
      Toast.show({ type: 'error', text1: 'Search failed', text2: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async (): Promise<void> => {
    if (loadingMore || hasReachedEnd) return;
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    try {
      const res = await getProfiles(buildParams(activeFiltersRef.current, nextPage) as any);
      const more: Profile[] = Array.isArray(res.data) ? res.data : [];
      setResults(prev => [...prev, ...more]);
      setCurrentPage(nextPage);
      setHasReachedEnd(more.length < PAGE_SIZE);
    } catch {
      Toast.show({ type: 'error', text1: 'Failed to load more results' });
    } finally {
      setLoadingMore(false);
    }
  };

  const BL = (o: { en: string; hi: string }) => o.hi ? `${o.en}  ${o.hi}` : o.en;
  const stateLabelMap: Record<string, string> = {};
  states.forEach(s => { stateLabelMap[s.en] = BL(s); });
  const districtLabelMap: Record<string, string> = {};
  districts.forEach(d => { districtLabelMap[d.en] = BL(d); });
  const gotraLabelMap: Record<string, string> = {};
  gotras.filter(g => g.active).forEach(g => { gotraLabelMap[g.gotra.en] = BL(g.gotra); });

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      {/* Header with mode toggle */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{t('screen.searchProfiles', 'Search Profiles')}</Text>
        <TouchableOpacity
          style={[styles.modeToggle, codeMode && styles.modeToggleActive]}
          onPress={() => { setCodeMode(m => !m); setProfileCode(''); }}
          activeOpacity={0.8}>
          <Icon name="qr-code-outline" size={16} color={codeMode ? '#fff' : THEME.primary} />
          <Text style={[styles.modeToggleText, codeMode && styles.modeToggleTextActive]}>Profile ID</Text>
        </TouchableOpacity>
      </View>

      {/* Profile Code Mode */}
      {codeMode ? (
        <View style={styles.codeSearchRow}>
          <View style={styles.codeSearchBox}>
            <Icon name="search-outline" size={18} color={THEME.textLight} />
            <TextInput
              style={styles.codeSearchInput}
              value={profileCode}
              onChangeText={v => setProfileCode(v.toUpperCase())}
              placeholder="Enter Profile ID (e.g. JS-1234)"
              placeholderTextColor={THEME.textLight}
              autoCapitalize="characters"
              returnKeyType="search"
              autoFocus
              onSubmitEditing={handleCodeSearch}
            />
            {profileCode.length > 0 && (
              <TouchableOpacity onPress={() => setProfileCode('')}>
                <Icon name="close-circle" size={18} color={THEME.textLight} />
              </TouchableOpacity>
            )}
          </View>
          <TouchableOpacity
            style={[styles.codeSearchBtn, !profileCode.trim() && { opacity: 0.5 }]}
            onPress={handleCodeSearch}
            disabled={!profileCode.trim() || loading}>
            {loading
              ? <ActivityIndicator size="small" color="#fff" />
              : <Icon name="arrow-forward" size={20} color="#fff" />}
          </TouchableOpacity>
        </View>
      ) : (
        /* Filter toggle row */
        <View style={styles.filterToggleRow}>
          <TouchableOpacity onPress={() => setShowFilters(f => !f)} style={styles.toggleBtn}>
            <Icon name={showFilters ? 'chevron-up' : 'options-outline'} size={20} color={THEME.primary} />
            <Text style={styles.toggleText}>{showFilters ? t('label.hide', 'Hide') : t('label.filters', 'Filters')}</Text>
            {!showFilters && activeFilterCount > 0 && (
              <View style={styles.filterBadge}>
                <Text style={styles.filterBadgeText}>{activeFilterCount}</Text>
              </View>
            )}
          </TouchableOpacity>
        </View>
      )}

      {showFilters && (
        <ScrollView style={styles.filterScroll} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">

          {/* Age Range */}
          <AgeRange
            label={t('field.ageRange', 'Age Range')}
            minAge={filters.minAge}
            maxAge={filters.maxAge}
            onMinChange={v => setFilter('minAge', v)}
            onMaxChange={v => setFilter('maxAge', v)}
          />

          {/* Religion — select first, caste loads below */}
          <ChipGroup
            label={t('field.religion', 'Religion')}
            options={RELIGION_OPTIONS}
            value={filters.religion}
            onChange={onReligionChange}
          />

          {/* Caste — only shown when a religion is selected */}
          {!!selectedReligion && (
            <DropdownPicker
              label={t('field.caste', 'Community / Caste')}
              value={filters.caste}
              options={casteOptions}
              placeholder="Any caste"
              onSelect={onCasteChange}
            />
          )}

          {/* Subgroup — only shown when a caste with subgroups is selected */}
          {!!selectedReligion && subgroupOptions.length > 1 && (
            <DropdownPicker
              label={t('field.casteSubType', 'Sub caste')}
              value={filters.casteSubType}
              options={subgroupOptions}
              placeholder="Any subgroup"
              onSelect={v => setFilter('casteSubType', v)}
            />
          )}

          {/* Marital Status */}
          <ChipGroup
            label={t('field.maritalStatus', 'Marital Status')}
            options={MARITAL_STATUS_FILTERS}
            value={filters.maritalStatus}
            onChange={v => setFilter('maritalStatus', v)}
          />

          {/* State → District cascade */}
          <DropdownPicker
            label={t('field.stateName', 'State')}
            value={filters.stateName}
            options={stateOptions}
            labelMap={stateLabelMap}
            onSelect={onStateSelected}
          />
          <DropdownPicker
            label={t('field.district', 'District')}
            value={filters.district}
            options={districtOptions.length > 0 ? ['Any', ...districtOptions] : []}
            placeholder={filters.stateName ? 'Any District' : 'Select state first'}
            disabled={!filters.stateName}
            loading={loadingDistricts}
            labelMap={districtLabelMap}
            onSelect={v => setFilter('district', v)}
          />

          {/* Education */}
          {educations.length > 0 && (
            <DropdownPicker
              label={t('field.highestEducation', 'Highest Education')}
              value={filters.highestEducation}
              options={['Any', ...educations.map(e => e.educationLevel.en)]}
              onSelect={v => { setFilter('highestEducation', v === 'Any' ? '' : v); setFilter('eduSubCategory', ''); }}
            />
          )}
          {educations.length > 0 && !!filters.highestEducation && (
            <DropdownPicker
              label={t('field.eduSubCategory', 'Education Specialization')}
              value={filters.eduSubCategory}
              options={['Any', ...(educations.find(e => e.educationLevel.en === filters.highestEducation)?.subCategories ?? []).map(s => s.en)]}
              onSelect={v => setFilter('eduSubCategory', v === 'Any' ? '' : v)}
            />
          )}

          {/* Gotra */}   
          <DropdownPicker
            label={t('field.gotra', 'Gotra')}
            value={filters.gotra}
            options={gotraOptions}
            labelMap={gotraLabelMap}
            onSelect={v => setFilter('gotra', v)}
          />

          <ChipGroup
            label={t('field.jobType', 'Employment Type')}
            options={JOB_TYPE_FILTERS}
            value={filters.jobType}
            onChange={v => setFilter('jobType', v)}
          />

          {professionSectors.length > 0 && (
            <DropdownPicker
              label={t('field.jobSector', 'Job Sector')}
              value={filters.jobSector}
              options={['Any', ...professionSectors.map(s => s.sector.en)]}
              onSelect={v => { setFilter('jobSector', v); setFilter('job', 'Any'); }}
            />
          )}
          {professionSectors.length > 0 && (
            <DropdownPicker
              label={t('field.job', 'Profession')}
              value={filters.job}
              options={['Any', ...(
                filters.jobSector !== 'Any'
                  ? (professionSectors.find(s => s.sector.en === filters.jobSector)?.professions ?? [])
                  : professionSectors.flatMap(s => s.professions)
              ).map(p => p.en)]}
              onSelect={v => setFilter('job', v)}
            />
          )}

          <ChipGroup
            label={t('field.manglik', 'Manglik')}
            options={MANGLIK_OPTIONS}
            value={filters.manglik}
            onChange={v => setFilter('manglik', v)}
          />

          <ChipGroup
            label={t('field.diet', 'Diet')}
            options={DIET_FILTERS}
            value={filters.diet}
            onChange={v => setFilter('diet', v)}
          />

          <ChipGroup
            label={t('field.smoking', 'Smoking')}
            options={SMOKING_FILTERS}
            value={filters.smoking}
            onChange={v => setFilter('smoking', v)}
          />

          <ChipGroup
            label={t('field.drinking', 'Drinking')}
            options={DRINKING_FILTERS}
            value={filters.drinking}
            onChange={v => setFilter('drinking', v)}
          />

          <View style={styles.chipSection}>
            <Text style={styles.filterLabel}>{t('field.minAnnualIncome', 'Min Annual Income')}</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.chipRow}>
                {INCOME_FILTER_OPTIONS.map(opt => (
                  <TouchableOpacity
                    key={opt.value}
                    style={[styles.chip, filters.minAnnualIncome === opt.value && styles.chipActive]}
                    onPress={() => setFilter('minAnnualIncome', opt.value)}>
                    <Text style={[styles.chipText, filters.minAnnualIncome === opt.value && styles.chipTextActive]}>{opt.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>

        </ScrollView>
      )}

      {/* Sticky footer — Search + Reset always visible */}
      {showFilters && !codeMode && (
        <View style={styles.stickyFooter}>
          <TouchableOpacity
            style={styles.resetBtn}
            onPress={() => {
              AsyncStorage.multiGet(['pref_looking_for', 'pref_search_religion', 'pref_search_caste']).then(pairs => {
                const gender = (pairs[0][1] === 'MALE' || pairs[0][1] === 'FEMALE')
                  ? pairs[0][1] : DEFAULT_FILTERS.gender;
                const religion = pairs[1][1] ?? DEFAULT_FILTERS.religion;
                const caste = pairs[2][1] ?? '';
                setFilters({ ...DEFAULT_FILTERS, gender, religion, caste, casteSubType: '', eduSubCategory: '', jobSector: 'Any', job: 'Any', diet: 'Any', smoking: 'Any', drinking: 'Any', minAnnualIncome: 0 });
                setDistrictOptions([]);
              });
            }}
            activeOpacity={0.8}>
            <Icon name="refresh-outline" size={18} color={THEME.primary} />
            <Text style={styles.resetBtnText}>Reset</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.searchBtn, loading && { opacity: 0.7 }]}
            onPress={handleSearch}
            disabled={loading}
            activeOpacity={0.85}>
            {loading
              ? <ActivityIndicator color="#fff" size="small" />
              : <Text style={styles.searchBtnText}>{t('btn.searchProfiles', 'Search Profiles')}</Text>}
          </TouchableOpacity>
        </View>
      )}

      {searched && !showFilters && (
        <FlatList<Profile>
          data={results}
          keyExtractor={(item: Profile) => item.id}
          renderItem={({ item }: ListRenderItemInfo<Profile>) => (
            <ProfileCard
              profile={item}
              onPress={() => navigation.navigate('ProfileDetail', { profileId: item.id })}
            />
          )}
          onEndReached={loadMore}
          onEndReachedThreshold={0.3}
          ListHeaderComponent={
            <View style={styles.resultHeader}>
              <Text style={styles.resultCount}>
                {results.length} {t('msg.profilesFound', 'profiles found')}
                {activeFilterCount > 0 ? ` · ${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}` : ''}
              </Text>
              <TouchableOpacity onPress={() => setShowFilters(true)}>
                <Text style={styles.editFilter}>{t('label.editFilters', 'Edit Filters')}</Text>
              </TouchableOpacity>
            </View>
          }
          ListFooterComponent={
            loadingMore ? (
              <ActivityIndicator color={THEME.primary} style={styles.footerLoader} />
            ) : hasReachedEnd && results.length > 0 ? (
              <Text style={styles.endText}>{t('msg.allResultsLoaded', 'All results loaded')}</Text>
            ) : null
          }
          ListEmptyComponent={
            <View style={styles.empty}>
              <Icon name="search-outline" size={50} color={THEME.border} />
              <Text style={styles.emptyText}>{t('msg.noMatchFound', 'No matches found')}</Text>
            </View>
          }
          contentContainerStyle={{ paddingBottom: 80 }}
        />
      )}
    </SafeAreaView>
  );
};

export default SearchScreen;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: THEME.bg },
  header: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 20, paddingTop: 16, paddingBottom: 16,
  },
  headerTitle: { fontSize: 24, fontWeight: '900', color: THEME.text },
  toggleBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, padding: 8 },
  toggleText: { color: THEME.primary, fontWeight: '700', fontSize: 14 },
  filterBadge: {
    backgroundColor: THEME.primary, borderRadius: 10,
    minWidth: 20, height: 20, alignItems: 'center', justifyContent: 'center',
    paddingHorizontal: 5, marginLeft: 2,
  },
  filterBadgeText: { color: '#fff', fontSize: 11, fontWeight: '800' },
  codeSearchRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 16, paddingBottom: 12,
  },
  codeSearchBox: {
    flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10,
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 12, paddingVertical: 10, backgroundColor: THEME.card,
  },
  codeSearchInput: { flex: 1, fontSize: 15, color: THEME.text, padding: 0 },
  codeSearchBtn: {
    backgroundColor: THEME.primary, borderRadius: 12,
    padding: 12, alignItems: 'center', justifyContent: 'center',
  },
  filterScroll: { flex: 1, paddingHorizontal: 16 },
  chipSection: { marginBottom: 20 },
  filterLabel: { fontSize: 14, fontWeight: '700', color: THEME.text, marginBottom: 10 },
  chipRow: { flexDirection: 'row', gap: 8 },
  chip: {
    paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20,
    backgroundColor: THEME.card, borderWidth: 1.5, borderColor: THEME.border,
  },
  chipActive: { backgroundColor: THEME.primary, borderColor: THEME.primary },
  chipText: { fontSize: 13, fontWeight: '600', color: THEME.textLight },
  chipTextActive: { color: '#fff' },
  // Age range
  ageRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  ageInput: {
    flex: 1, borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 12, fontSize: 16, fontWeight: '700',
    color: THEME.text, backgroundColor: THEME.card, textAlign: 'center',
  },
  ageInputActive: { borderColor: THEME.primary },
  ageInputError: { borderColor: '#E53E3E' },
  ageErrorText: { fontSize: 12, color: '#E53E3E', fontWeight: '600', marginTop: 6 },
  ageDivider: { width: 16, height: 2, backgroundColor: THEME.border, borderRadius: 2 },
  ageUnit: { fontSize: 14, color: THEME.textLight, fontWeight: '600', width: 28 },
  // Dropdown button
  dropdownBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 13, backgroundColor: THEME.card,
  },
  dropdownBtnActive: { borderColor: THEME.primary },
  dropdownBtnDisabled: { opacity: 0.5 },
  dropdownBtnText: { fontSize: 15, color: THEME.textLight, flex: 1 },
  dropdownBtnTextActive: { color: THEME.primary, fontWeight: '600' },
  // Modal
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  modalSheet: {
    backgroundColor: THEME.bg, borderTopLeftRadius: 20, borderTopRightRadius: 20,
    height: '75%',
  },
  modalHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 20, paddingTop: 20, paddingBottom: 12,
    borderBottomWidth: 1, borderColor: THEME.border,
  },
  modalTitle: { fontSize: 17, fontWeight: '800', color: THEME.text },
  modalSearch: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    marginHorizontal: 16, marginVertical: 12,
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 12, paddingVertical: 10, backgroundColor: THEME.card,
  },
  modalSearchInput: { flex: 1, fontSize: 15, color: THEME.text, padding: 0 },
  modalOption: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 20, paddingVertical: 14,
    borderBottomWidth: 1, borderColor: THEME.border,
  },
  modalOptionActive: { backgroundColor: '#FAE5E5' },
  modalOptionText: { fontSize: 15, color: THEME.text },
  modalOptionTextActive: { color: THEME.primary, fontWeight: '700' },
  modalEmpty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 48, gap: 12 },
  modalEmptyText: { fontSize: 14, color: THEME.textLight, textAlign: 'center' },
  // Results
  resultHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 16, paddingVertical: 12,
  },
  resultCount: { fontSize: 15, fontWeight: '700', color: THEME.text },
  editFilter: { color: THEME.primary, fontWeight: '700', fontSize: 14 },
  empty: { alignItems: 'center', marginTop: 60, gap: 12 },
  emptyText: { fontSize: 15, color: THEME.textLight },
  footerLoader: { paddingVertical: 20 },
  endText: { textAlign: 'center', fontSize: 13, color: THEME.textLight, paddingVertical: 16 },
  // Mode toggle (Profile ID vs filter search)
  modeToggle: {
    flexDirection: 'row', alignItems: 'center', gap: 5,
    paddingHorizontal: 12, paddingVertical: 7, borderRadius: 20,
    borderWidth: 1.5, borderColor: THEME.primary,
  },
  modeToggleActive: { backgroundColor: THEME.primary },
  modeToggleText: { fontSize: 13, fontWeight: '700', color: THEME.primary },
  modeToggleTextActive: { color: '#fff' },
  filterToggleRow: {
    paddingHorizontal: 16, paddingBottom: 8,
  },
  stickyFooter: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingHorizontal: 16, paddingVertical: 12,
    backgroundColor: THEME.card,
    borderTopWidth: 1, borderTopColor: THEME.border,
  },
  resetBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 16, paddingVertical: 14, borderRadius: 12,
    borderWidth: 1.5, borderColor: THEME.primary, backgroundColor: THEME.card,
  },
  resetBtnText: { fontSize: 14, fontWeight: '700', color: THEME.primary },
  searchBtn: {
    flex: 1, paddingVertical: 14, borderRadius: 12,
    backgroundColor: THEME.primary, alignItems: 'center', justifyContent: 'center',
  },
  searchBtnText: { fontSize: 15, fontWeight: '800', color: '#fff' },
});
